/**
 * Family Bible App Color Palette
 * 
 * A carefully curated color palette for the Family Bible App
 * Based on the provided color scheme with meaningful names
 */

export const colors = {
  // Primary Colors
  aquamarine: '#A5FFD6',    // Light mint green - for accents and highlights
  cerulean: '#247BA0',      // Medium blue - for primary actions and branding
  spaceCadet: '#063354',    // Dark navy - for text and headers (cassette body color)
  
  // Cerulean Light Shades
  ceruleanLight: '#4A9BC7', // Lighter cerulean
  ceruleanLighter: '#6BB6D6', // Even lighter cerulean  
  ceruleanLightest: '#8CC7E5', // Lightest cerulean
  
  // Secondary Colors  
  mintGreen: '#CDFFF9',     // Very light blue-green - for backgrounds
  timberwolf: '#E7DEDA',    // Warm beige - for neutral backgrounds
  coral: '#FD494F',         // Vibrant red-coral - for alerts and highlights
  
  // Semantic Colors (using the palette)
  primary: {
    50: '#CDFFF9',   // mintGreen - lightest
    100: '#A5FFD6',  // aquamarine - light
    200: '#7FFFD4',  // aquamarine variant
    300: '#40E0D0',  // turquoise variant
    400: '#20B2AA',  // light sea green
    500: '#247BA0',  // cerulean - main primary
    600: '#1E6B8A',  // cerulean darker
    700: '#1A5A73',  // cerulean even darker
    800: '#063354',  // spaceCadet - darkest (cassette body color)
    900: '#0F1A2E',  // spaceCadet darker
  },
  
  secondary: {
    50: '#E7DEDA',   // timberwolf - lightest
    100: '#D4C4B8',  // timberwolf variant
    200: '#C1AA96',  // timberwolf variant
    300: '#AE9074',  // timberwolf variant
    400: '#9B7652',  // timberwolf variant
    500: '#885C30',  // timberwolf variant
    600: '#6B4724',  // timberwolf variant
    700: '#4E3218',  // timberwolf variant
    800: '#311D0C',  // timberwolf variant
    900: '#140800',  // timberwolf darkest
  },
  
  // Neutral colors using the palette
  gray: {
    50: '#CDFFF9',   // mintGreen
    100: '#A5FFD6',  // aquamarine
    200: '#E7DEDA',  // timberwolf
    300: '#D4C4B8',  // timberwolf variant
    400: '#C1AA96',  // timberwolf variant
    500: '#AE9074',  // timberwolf variant
    600: '#9B7652',  // timberwolf variant
    700: '#6B4724',  // timberwolf variant
    800: '#063354',  // spaceCadet (cassette body color)
    900: '#0F1A2E',  // spaceCadet darker
  }
};

// CSS Custom Properties for easy use in CSS
export const cssVariables = `
  :root {
    --color-aquamarine: ${colors.aquamarine};
    --color-cerulean: ${colors.cerulean};
    --color-space-cadet: ${colors.spaceCadet};
    --color-mint-green: ${colors.mintGreen};
    --color-timberwolf: ${colors.timberwolf};
    --color-coral: ${colors.coral};
    
    --color-cerulean-light: ${colors.ceruleanLight};
    --color-cerulean-lighter: ${colors.ceruleanLighter};
    --color-cerulean-lightest: ${colors.ceruleanLightest};
    
    --color-primary-50: ${colors.primary[50]};
    --color-primary-100: ${colors.primary[100]};
    --color-primary-200: ${colors.primary[200]};
    --color-primary-300: ${colors.primary[300]};
    --color-primary-400: ${colors.primary[400]};
    --color-primary-500: ${colors.primary[500]};
    --color-primary-600: ${colors.primary[600]};
    --color-primary-700: ${colors.primary[700]};
    --color-primary-800: ${colors.primary[800]};
    --color-primary-900: ${colors.primary[900]};
    
    --color-secondary-50: ${colors.secondary[50]};
    --color-secondary-100: ${colors.secondary[100]};
    --color-secondary-200: ${colors.secondary[200]};
    --color-secondary-300: ${colors.secondary[300]};
    --color-secondary-400: ${colors.secondary[400]};
    --color-secondary-500: ${colors.secondary[500]};
    --color-secondary-600: ${colors.secondary[600]};
    --color-secondary-700: ${colors.secondary[700]};
    --color-secondary-800: ${colors.secondary[800]};
    --color-secondary-900: ${colors.secondary[900]};
  }
`;

// Tailwind color classes mapping
export const tailwindColors = {
  aquamarine: colors.aquamarine,
  cerulean: colors.cerulean,
  'space-cadet': colors.spaceCadet,
  'mint-green': colors.mintGreen,
  timberwolf: colors.timberwolf,
  coral: colors.coral,
  'cerulean-light': colors.ceruleanLight,
  'cerulean-lighter': colors.ceruleanLighter,
  'cerulean-lightest': colors.ceruleanLightest,
};

export default colors;
